/**
 * National phone number digit lengths per ISO 3166-1 alpha-2 country code.
 * "national digits" = digits after the country calling code.
 * Countries with variable lengths use { min, max }.
 */
export const PHONE_NATIONAL_LENGTH: Record<
  string,
  { min: number; max: number }
> = {
  // Europe
  FR: { min: 9, max: 9 },
  DE: { min: 10, max: 11 },
  GB: { min: 10, max: 10 },
  ES: { min: 9, max: 9 },
  IT: { min: 9, max: 10 },
  PT: { min: 9, max: 9 },
  BE: { min: 8, max: 9 },
  NL: { min: 9, max: 9 },
  CH: { min: 9, max: 9 },
  AT: { min: 10, max: 13 },
  PL: { min: 9, max: 9 },
  SE: { min: 7, max: 13 },
  NO: { min: 8, max: 8 },
  DK: { min: 8, max: 8 },
  FI: { min: 9, max: 11 },
  IE: { min: 9, max: 9 },
  LU: { min: 8, max: 9 },
  GR: { min: 10, max: 10 },
  CZ: { min: 9, max: 9 },
  RO: { min: 9, max: 9 },
  HU: { min: 8, max: 9 },
  BG: { min: 8, max: 9 },
  HR: { min: 8, max: 9 },
  SK: { min: 9, max: 9 },
  SI: { min: 8, max: 8 },
  LT: { min: 8, max: 8 },
  LV: { min: 8, max: 8 },
  EE: { min: 7, max: 8 },
  CY: { min: 8, max: 8 },
  MT: { min: 8, max: 8 },
  IS: { min: 7, max: 7 },
  MC: { min: 8, max: 9 },
  AD: { min: 6, max: 9 },
  // North America
  US: { min: 10, max: 10 },
  CA: { min: 10, max: 10 },
  MX: { min: 10, max: 10 },
  // South America
  BR: { min: 10, max: 11 },
  AR: { min: 10, max: 10 },
  CL: { min: 9, max: 9 },
  CO: { min: 10, max: 10 },
  PE: { min: 9, max: 9 },
  // Asia
  CN: { min: 11, max: 11 },
  JP: { min: 10, max: 10 },
  KR: { min: 10, max: 11 },
  IN: { min: 10, max: 10 },
  SG: { min: 8, max: 8 },
  HK: { min: 8, max: 8 },
  TW: { min: 9, max: 10 },
  TH: { min: 9, max: 9 },
  MY: { min: 9, max: 10 },
  PH: { min: 10, max: 10 },
  ID: { min: 10, max: 12 },
  VN: { min: 9, max: 10 },
  // Middle East / Africa
  AE: { min: 9, max: 9 },
  SA: { min: 9, max: 9 },
  QA: { min: 8, max: 8 },
  KW: { min: 8, max: 8 },
  BH: { min: 8, max: 8 },
  OM: { min: 8, max: 8 },
  IL: { min: 9, max: 10 },
  TR: { min: 10, max: 10 },
  EG: { min: 10, max: 10 },
  MA: { min: 9, max: 9 },
  TN: { min: 8, max: 8 },
  DZ: { min: 9, max: 9 },
  NG: { min: 10, max: 10 },
  ZA: { min: 9, max: 9 },
  KE: { min: 9, max: 9 },
  GH: { min: 9, max: 9 },
  CI: { min: 10, max: 10 },
  SN: { min: 9, max: 9 },
  CM: { min: 9, max: 9 },
  // Oceania
  AU: { min: 9, max: 9 },
  NZ: { min: 8, max: 10 },
};

/**
 * Returns the max total digits (country code + national) allowed for a country.
 * Falls back to E.164 max (15) if the country is not mapped.
 */
export function getMaxPhoneDigits(
  countryCode: string | null,
  countryCallingCode: string | null,
): number {
  if (!countryCode || !countryCallingCode) return 15;
  const rule = PHONE_NATIONAL_LENGTH[countryCode.toUpperCase()];
  if (!rule) return 15;
  return countryCallingCode.length + rule.max;
}

/**
 * Returns the min total digits (country code + national) for validation.
 * Falls back to a sensible minimum (country code + 4) if not mapped.
 */
export function getMinPhoneDigits(
  countryCode: string | null,
  countryCallingCode: string | null,
): number {
  if (!countryCode || !countryCallingCode) return 7;
  const rule = PHONE_NATIONAL_LENGTH[countryCode.toUpperCase()];
  if (!rule) return countryCallingCode.length + 4;
  return countryCallingCode.length + rule.min;
}
